import { ClientConfig } from "~/config/clientConfig";
import {
  useGetClientConfigQuery,
  useCreateClientConfigMutation,
  useUpdateClientConfigMutation
} from "~/redux/warehouse/clientConfig.hooks";

/**
 * Helper for implementing the proper ETag concurrency flow as specified in backend tests.
 *
 * Flow:
 * 1. GET to check if config exists and get ETag
 * 2a. If 404: POST without If-Match header (first-time creation)
 * 2b. If 200: POST with If-Match header using ETag from step 1 (update)
 * 3. Handle 409 conflicts by retrying from step 1
 */
export const useClientConfigETagFlow = () => {
  const {
    data: existingConfig,
    error: getError,
    refetch
  } = useGetClientConfigQuery();
  const [createConfig] = useCreateClientConfigMutation();
  const [updateConfig] = useUpdateClientConfigMutation();

  const saveConfig = async (
    config: ClientConfig,
    maxRetries = 3
  ): Promise<void> => {
    let retryCount = 0;

    while (retryCount < maxRetries) {
      try {
        // Step 1: Always refetch to get latest ETag
        const { data: currentConfig, error } = await refetch();

        // Step 2a: Config doesn't exist (404) - create without If-Match header
        if (error && "status" in error && error.status === 404) {
          await createConfig({
            ClientConfiguration: JSON.stringify(config)
          }).unwrap();
          return; // Success
        }

        // Step 2b: Config exists (200) - update with If-Match header
        if (currentConfig?.etag) {
          await updateConfig({
            ClientConfiguration: JSON.stringify(config),
            etag: currentConfig.etag
          }).unwrap();
          return; // Success
        }

        // Unexpected state - no error but no ETag
        throw new Error("Config exists but no ETag found in response");
      } catch (error) {
        // Step 3: Handle 409 conflict - retry with fresh GET/ETag
        if (
          error &&
          typeof error === "object" &&
          "status" in error &&
          (error as { status: number }).status === 409
        ) {
          retryCount++;
          console.warn(
            `ETag conflict, retrying... (${retryCount}/${maxRetries})`
          );

          if (retryCount >= maxRetries) {
            throw new Error(
              `Failed to save config after ${maxRetries} retries due to ETag conflicts`
            );
          }

          // Wait briefly before retry to reduce conflict probability
          await new Promise((resolve) => setTimeout(resolve, 100 * retryCount));
          continue;
        }

        // Non-409 error - throw immediately
        throw error;
      }
    }
  };

  return {
    existingConfig,
    getError,
    saveConfig,
    createConfig,
    updateConfig
  };
};

/**
 * Example usage for auto-create hook:
 *
 * const { saveConfig } = useClientConfigETagFlow();
 *
 * // For 404 case (auto-creation)
 * try {
 *   await saveConfig(configToUpload);
 *   console.log('Config created successfully');
 * } catch (error) {
 *   if (error.status === 409) {
 *     console.log('Config already exists (409 conflict)');
 *   } else {
 *     console.error('Failed to create config:', error);
 *   }
 * }
 */
